import { useEffect, useState } from "react";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import FormField from "../../../components/common/FormField";
import useAsync from "../../../hooks/useAsync";
import useFormDraft from "../../../hooks/useFormDraft";
import DraftNotice from "../../../components/common/DraftNotice";
import { STAGE_LABELS, STAGE_ORDER } from "../../../utils/constants";
import { validateCycle, hasErrors } from "../../../utils/validators";
import { getCycleConfigOptions } from "../../../services/cycleApi";
import { getLookups } from "../../../services/employeeApi";
import { DEFAULT_SECTIONS } from "../../../services/core";
import Select from "../../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { getCycleConfigOptions, getLookups };

const SECTION_LABELS = {
  summary: "Summary / key achievements",
  strengths: "Strengths",
  improvement_areas: "Challenges / areas to improve",
  development_plan: "Development plan",
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// [UPDATED] Performance cycles now run from October to March (for example Oct 2026 - Mar 2027).
// From October to March the current cycle is offered; from April to September the next one (starting 1 October).
export function defaultCycleDates(today = new Date()) {
  const month = today.getMonth(); // 0 = January
  const y = month >= 9 ? today.getFullYear() : month <= 2 ? today.getFullYear() - 1 : today.getFullYear();
  const start = `${y}-10-01`;
  const end = `${y + 1}-03-31`;
  return { y, start, end, name: `${MONTH[9]} ${y} - ${MONTH[2]} ${y + 1}` };
}

function blankCycle() {
  const today = new Date();
  const { y, start, end, name } = defaultCycleDates(today);
  const goalStart = ymd(today) > start ? ymd(today) : start;
  const in30 = new Date(`${goalStart}T00:00:00`); in30.setDate(in30.getDate() + 30);
  const goalEnd = ymd(in30) < end ? ymd(in30) : end;
  return {
    name,
    startDate: start,
    endDate: end,
    scoringPolicyId: "",
    ratingSchemeId: "",
    workflow: {
      release_mode: "MANUAL",
      acknowledgment_required: true,
      manager_feedback_visible: true,
      assessment_sections: DEFAULT_SECTIONS,
      hr_owner_employee_id: "",
      progress_approval_required: true,
    },
    stages: [
      { key: "goalSetting", startDate: goalStart, endDate: goalEnd, status: "upcoming" },
      { key: "selfAssessment", startDate: `${y + 1}-02-01`, endDate: `${y + 1}-02-28`, status: "upcoming" },
      { key: "managerReview", startDate: `${y + 1}-03-01`, endDate: `${y + 1}-03-15`, status: "upcoming" },
      { key: "hrFinalization", startDate: `${y + 1}-03-16`, endDate: `${y + 1}-03-31`, status: "upcoming" },
    ],
  };
}

export default function CycleFormModal({ open, cycle, onClose, onSave }) {
  const [form, setForm] = useState(blankCycle);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const options = useAsync(() => api.getCycleConfigOptions(), [open], { skip: !open });
  const people = useAsync(() => api.getLookups(), [open], { skip: !open });

  useEffect(() => {
    if (open) {
      setForm(cycle
        ? { ...JSON.parse(JSON.stringify(cycle)), scoringPolicyId: String(cycle.scoringPolicyId || ""), ratingSchemeId: String(cycle.ratingSchemeId || ""), workflow: { ...blankCycle().workflow, ...cycle.workflow, progress_approval_required: true } }
        : blankCycle());
      setErrors({});
    }
  }, [open, cycle]);

  useEffect(() => {
    if (!options.data) return;
    setForm((f) => ({
      ...f,
      scoringPolicyId: f.scoringPolicyId || options.data.policies.find((p) => p.status === "APPROVED")?.id || "",
      ratingSchemeId: f.ratingSchemeId || options.data.schemes.find((s) => s.status === "APPROVED")?.id || "",
    }));
  }, [options.data]);

  // [NEW] Keep unsaved cycle settings; restored after the policy/scheme defaults have loaded.
  const draft = useFormDraft(`cycle:${cycle?.id || "new"}`, form, setForm, { active: Boolean(open && options.data) });

  const setStage = (key, field) => (e) =>
    setForm((f) => ({ ...f, stages: f.stages.map((s) => (s.key === key ? { ...s, [field]: e.target.value } : s)) }));
  const setWorkflow = (key, value) => setForm((f) => ({ ...f, workflow: { ...f.workflow, [key]: value } }));
  const toggleSection = (key) => setWorkflow(
    "assessment_sections",
    form.workflow.assessment_sections.includes(key)
      ? form.workflow.assessment_sections.filter((k) => k !== key)
      : [...form.workflow.assessment_sections, key]
  );

  const submit = async () => {
    const found = validateCycle(form);
    if (!form.scoringPolicyId) found.scoringPolicyId = "Choose a scoring policy.";
    if (!form.ratingSchemeId) found.ratingSchemeId = "Choose a rating scheme.";
    if (!form.workflow.hr_owner_employee_id) found.hrOwner = "Choose the HR owner.";
    if (!form.workflow.assessment_sections.length) found.sections = "Keep at least one section.";
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    const ok = await onSave(form);
    setSaving(false);
    if (ok) { draft.clear(); onClose(); }
  };

  const ordered = STAGE_ORDER.map((k) => form.stages.find((s) => s.key === k)).filter(Boolean);
  const policies = options.data?.policies || [];
  const schemes = options.data?.schemes || [];

  return (
    <Modal
      open={open}
      size="lg"
      title={cycle ? `Edit ${cycle.name}` : "New performance cycle"}
      subtitle="Each stage accepts submissions only between its dates. Draft cycles can be edited until employees are added."
      onClose={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>{cycle ? "Save cycle" : "Create cycle"}</Button>
        </>
      }
    >
      <DraftNotice draft={draft} />
      <div className="form-grid">
        <FormField label="Cycle name" required error={errors.name} className="span-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="Start date" required error={errors.startDate} hint="Cycles run October to March, e.g. 1 Oct 2026.">
          <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
        </FormField>
        <FormField label="End date" required error={errors.endDate} hint="e.g. 31 Mar 2027. Every stage window must fall inside these dates.">
          <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </FormField>
      </div>
      <h3 className="section-title">Stage windows</h3>
      <div className="stage-edit-list">
        {ordered.map((s, i) => (
          <div key={s.key} className="stage-edit-row">
            <span className="stage-edit-num">{i + 1}</span>
            <span className="stage-edit-name">{STAGE_LABELS[s.key]}</span>
            <input type="date" className="form-control" value={s.startDate} onChange={setStage(s.key, "startDate")} aria-label={`${STAGE_LABELS[s.key]} start`} />
            <span className="muted">to</span>
            <input type="date" className="form-control" value={s.endDate} onChange={setStage(s.key, "endDate")} aria-label={`${STAGE_LABELS[s.key]} end`} />
            {errors[`stage_${s.key}`] && <p className="form-error stage-edit-error">{errors[`stage_${s.key}`]}</p>}
          </div>
        ))}
      </div>

      <h3 className="section-title">Scoring and workflow</h3>
      <div className="form-grid">
        <FormField label="Scoring policy" required error={errors.scoringPolicyId} hint="Create and approve versions under Scoring & ratings.">
          <Select value={form.scoringPolicyId} onChange={(e) => setForm({ ...form, scoringPolicyId: e.target.value })}>
            <option value="">Choose a policy</option>
            {policies.map((p) => <option key={p.id} value={p.id}>{p.name}{p.status !== "APPROVED" ? " (draft)" : ""}</option>)}
          </Select>
        </FormField>
        <FormField label="Rating scheme" required error={errors.ratingSchemeId}>
          <Select value={form.ratingSchemeId} onChange={(e) => setForm({ ...form, ratingSchemeId: e.target.value })}>
            <option value="">Choose a scheme</option>
            {schemes.map((s) => <option key={s.id} value={s.id}>{s.name}{s.status !== "APPROVED" ? " (draft)" : ""}</option>)}
          </Select>
        </FormField>
        <FormField label="HR owner" required error={errors.hrOwner} hint="Receives manager submissions for finalization.">
          <Select value={form.workflow.hr_owner_employee_id} onChange={(e) => setWorkflow("hr_owner_employee_id", e.target.value)}>
            <option value="">Choose a person</option>
            {(people.data?.managers || []).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
          </Select>
        </FormField>
        <FormField label="Result release">
          <Select value={form.workflow.release_mode} onChange={(e) => setWorkflow("release_mode", e.target.value)}>
            <option value="MANUAL">HR releases each result</option>
            <option value="ON_FINALIZE">Release automatically when finalized</option>
          </Select>
        </FormField>
        <label className="checkbox">
          <input type="checkbox" checked={form.workflow.acknowledgment_required} onChange={(e) => setWorkflow("acknowledgment_required", e.target.checked)} />
          <span>Employees must acknowledge their result</span>
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={form.workflow.manager_feedback_visible} onChange={(e) => setWorkflow("manager_feedback_visible", e.target.checked)} />
          <span>Show the manager's written feedback to the employee</span>
        </label>
        <label className="checkbox">
          {/* [UPDATED] Always on: every cycle carries workflow_config.progress_approval_required = true */}
          <input type="checkbox" checked disabled readOnly />
          <span>Progress updates need the manager&apos;s approval before they count (always on)</span>
        </label>
      </div>
      <FormField label="Assessment sections" error={errors.sections} hint="Free-text boxes both the employee and the manager must fill in.">
        <div className="rule-channels">
          {DEFAULT_SECTIONS.map((key) => (
            <label key={key} className="checkbox">
              <input type="checkbox" checked={form.workflow.assessment_sections.includes(key)} onChange={() => toggleSection(key)} />
              <span>{SECTION_LABELS[key]}</span>
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}
